<template>
  <div class="request-summary" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar step7 />

    <!-- Loading spinner -->
    <div class="form-container" v-if="globalLoading">
      <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
    </div>

    <!-- Request summary -->
    <div v-if="!globalLoading" class="form-container request-summary-form">

      <!-- Information -->
      <div class="form-header">
        <span v-translate>
          Please review your request. We want to process your request as soon as possible, so please
          make sure you have included all required details. If we are missing
          information, it will slow down the process.
        </span>
      </div>

      <!-- Document requirements -->
      <div
        v-if="
          organizationDetails.eventDocumentationEnabled ||
          organizationDetails.expenseDocumentationEnabled
        "
        class="form-field card-shape"
      >
        <h4 class="form-field-header" v-translate>Your documents must:</h4>
        <div class="form-field-container document-requirements">
          <ul>
            <li
              v-if="
                organizationDetails.expenseDocumentationEnabled &&
                organizationDetails.eventDocumentationEnabled
              "
              v-translate
            >
              Describe the emergency and expense
            </li>
            <li
              v-if="
                !organizationDetails.expenseDocumentationEnabled &&
                organizationDetails.eventDocumentationEnabled
              "
              v-translate
            >
              Describe the emergency
            </li>
            <li
              v-if="
                organizationDetails.expenseDocumentationEnabled &&
                !organizationDetails.eventDocumentationEnabled
              "
              v-translate
            >
              Describe the expense
            </li>
            <li v-translate>
              Clearly show your name or connect you to the person whose name is on the document
            </li>
            <li v-translate v-if="organizationDetails.expenseDocumentationEnabled">
              Show biller information (address, logo, etc.)
            </li>
            <li v-translate>
              Show emergency date, bill date, and/or payment due date (as applicable)
            </li>
            <li v-translate>
              Be readable - if you are uploading a screenshot or a photo, make sure it includes the
              entire document or screen view, including headers and footers
            </li>
            <li v-translate>
              Not contain social security numbers, bank account numbers, user names, or passwords -
              please black out or remove before submitting
            </li>
          </ul>
        </div>
      </div>

      <!-- Messages -->
      <div class="form-container">
        <div class="form-field view-messages">
          <a @click="openMessageCenter" v-translate>View messages</a>&nbsp;<div class="unread-messages" @click="openMessageCenter">{{ messageUnreadCount() }}</div>
        </div>
      </div>

      <!-- Request details -->
      <div class="request-details">
        <!-- PayPal information -->
        <div class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Payment Details</h4>
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'PayPalVerify' }" v-translate>
              Edit
            </router-link>
          </div>
          <div class="form-field-container">
            <div class="field-name" v-translate>Payment Method</div>
            <div class="field-value">PayPal</div>
          </div>
          <div class="form-field-container">
            <div class="field-name" v-translate>PayPal Email Address</div>
            <div class="field-value">{{ currentRequest.paypalEmail }}</div>
          </div>
        </div>

        <!-- Selected event categories -->
        <div class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Your emergencies</h4>
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'EventCategories' }" v-translate>
              Edit
            </router-link>
          </div>
          <div v-for="event in events" :key="event.id" class="event-category-name">
            {{ event.categoryRef.name }}
          </div>
          <div v-if="!events.length" class="event-category-name" v-translate>- No Emergencies Selected -</div>
        </div>

        <!-- Event categories -->
        <div class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Emergency details</h4>
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'CreateEvents' }" v-translate>
              Edit
            </router-link>
          </div>

          <!-- Display each selected event category -->
          <div v-for="event in eventsExceptCOVID" :key="event.id" class="event-details">
            <!-- Event name -->
            <div class="form-field event-category-name">
              {{ event.categoryRef.name }}
            </div>

            <!-- When it happend (except for Covid)-->
            <div
              v-if="!event.isCovid19() && organizationDetails.enableWhenItHappenedEventQuestion"
              class="form-field-container"
            >
              <div class="field-name" v-translate>When it happened</div/>
              <div class="field-value">{{ event.dateOccurred }}</div>
            </div>

            <!-- Display each event question & answer -->
            <div
              v-for="question in event.questions"
              :key="question.id"
              class="form-field-container"
            >
              <!-- Question -->
              <div class="field-name">
                {{question.text}}
              </div>

              <!-- Answers (translated) -->
              <div
                v-if="question.qType !== 'DATE' && question.qType !== 'FREE_TEXT' && question.qType !== 'NUMBER'"
                class="field-value"
              >
                {{question.answer.text}}
              </div>
              <!-- Answers (user defined) -->
              <div
                v-else
                class="field-value"
              >
                {{ question.answer.text }}
              </div>
            </div>
          </div>
        </div>

        <!-- Additional questions -->
        <div class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Additional Questions</h4>
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'CreateEvents' }" v-translate>
              Edit
            </router-link>
          </div>
          <template v-if="!legacyRequestQuestionsCheck">
            <div
              class="form-field-container"
              v-for="question in currentRequest.requestQuestions"
              :key="question.id"
            >
              <div class="field-name">
                {{question.text}}
              </div>
              <div class="field-value">
                {{ getSelectedOption(question.answer.text) || noAnswer }}
              </div>
            </div>
          </template>

          <!-- Legacy request additional questions -->
          <template v-if="legacyRequestQuestionsCheck">
            <div class="form-field-container">
              <div class="field-name" v-translate>
                Please explain how the emergency or emergencies you selected created a financial
                hardship for you and your household? That is, how did they make it hard for you to
                afford basic living expenses?
              </div>
              <div class="field-value" v-translate>
                {{ currentRequest.eventsFinancialHardshipAnswer || noAnswer }}
              </div>
            </div>
            <div class="form-field-container">
              <div class="field-name" v-translate>
                Was at least one of the emergencies described above unexpected or outside of your
                control?
              </div>
              <div class="field-value" v-translate>
                {{ currentRequest.eventsUnexpectedOrUncontrollableMisc || noAnswer }}
              </div>
            </div>
            <div class="form-field-container">
              <div class="field-name" v-translate>
                Has the emergency or emergencies made it difficult for you to afford basic living
                expenses?
              </div>
              <div class="field-value" v-translate>
                {{ currentRequest.eventsBasicLivingDifficultiesMisc || noAnswer }}
              </div>
            </div>
            <div class="form-field-container">
              <div class="field-name" v-translate>
                Is there anything else you’d like to share with us about your emergency or
                emergencies?
              </div>
              <div class="field-value">
                {{ currentRequest.eventsMisc || noAnswer }}
              </div>
            </div>
          </template>
        </div>

        <!-- Event documentation -->
        <div v-if="organizationDetails.eventDocumentationEnabled" class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Emergency Documents</h4>

          <!-- Edit link -->
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'EventDocumentation' }" v-translate>
              Edit
            </router-link>
          </div>

          <!-- Display each selected event category & relative documents -->
          <div v-for="event in eventsExceptCOVID" :key="event.id" class="event-details">
            <!-- Event name -->
            <div class="form-field event-category-name">
              {{ event.categoryRef.name }}
            </div>

            <!-- Uploaded documents list -->
            <div class="form-field-container">
              <div class="field-name" v-translate>Uploaded Documents</div>

              <!-- Display each uploaded document -->
              <div v-for="doc in event.documentsRef" :key="doc.id" class="field-value">
                <a :href="doc.file" download target="_blank" class="upload-link">{{ doc.filename }}</a>
              </div>
              <div v-if="!event.documentsRef.length" class="field-value" v-translate>- No Documents Provided -</div>
            </div>
          </div>

          <!-- Additional information -->
          <div class="form-field-container">
            <div class="field-name" v-translate>
              Please add any additional information that will help us understand your document(s):
            </div>
            <div class="field-value">
              {{ currentRequest.eventDocumentationMisc || noAnswer }}
            </div>
          </div>
        </div>

        <!-- Expense documentation -->
        <div class="form-field card-shape">
          <h4 class="form-field-header" v-translate>Expenses</h4>

          <!-- Edit link -->
          <div v-if="canEditRequest" class="edit-link">
            <router-link :to="{ name: 'ExpenseCategories' }" v-translate>
              Edit
            </router-link>
          </div>

          <!-- Display each expense category, amount & relative documents -->
          <div v-for="expense in currentRequest.expensesRef" :key="expense.id" class="expense-details">
            <!-- Expense category  -->
            <div class="form-field event-category-name">
              {{ expense.categoryRef.name }}
            </div>

            <!-- Expense amount -->
            <div class="form-field-container">
              <div class="field-name">{{ $gettext('amount requested') }}</div>
              <div class="field-value">{{ expense.amount | currency }}</div>
            </div>

            <!-- Uploaded expense documents list -->
            <div v-if="organizationDetails.expenseDocumentationEnabled" class="form-field-container">
              <div class="field-name" v-translate>Uploaded Documents</div>

              <!-- Display each uploaded document -->
              <div v-for="doc in expense.documentsRef" :key="doc.id" class="field-value">
                <a :href="doc.file" download target="_blank" class="upload-link">{{ doc.filename }}</a>
              </div>
              <div v-if="!expense.documentsRef.length" class="field-value" v-translate>- No Documents Provided -</div>
            </div>
          </div>
          <div v-if="!currentRequest.expensesRef.length" class="form-field event-category-name" v-translate>
            - No Expenses Selected -
          </div>

          <!-- Additional information -->
          <div class="form-field-container">
            <div class="field-name" v-translate>
              Please add any additional information that will help us understand your document(s):
            </div>
            <div class="field-value">
              {{ currentRequest.expenseDocumentationMisc || noAnswer }}
            </div>
          </div>
        </div>

        <div class="form-field total-grant-amount">
          <div class="label" v-translate>Total amount requested</div>
          <div class="amount">{{ this.currentRequest.amount | currency }}</div>
        </div>

        <!-- Event documentation error -->
        <div v-if="organizationDetails.eventDocumentationEnabled && eventDocRequired" class="form-field">
          <span class="text-error-msg" v-translate>
            Documentation is required for this request. Please go back and upload documents for at least
            one emergency.
          </span>
        </div>

        <!-- Expense documentation error -->
        <div v-if="expenseDocRequired && requestHasUncommonEventCategories" class="form-field">
          <span class="text-error-msg" v-translate>
            Required documentation is missing for at least one expense category; please go back and
            upload the missing documents.
          </span>
        </div>
      </div>

      <!-- Submit -->
      <div class="button-container">
        <button
          v-if="!requestAlreadySubmitted"
          @click="onSubmitRequest"
          class="form-button"
          data-action="commit-request-summary"
        >
          <span v-if="!loading" v-translate>Submit</span>
          <span v-else>
            <LoadSpinner color="white" />
          </span>
        </button>
      </div>
    </div>

    <!-- Messaging center modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        padding: 0,
        overflow: 'hidden scroll',
      }"
      :additionalContainerStyles="{
        zIndex: '1001',
        padding: 0,
      }"
      v-if="mode === MESSAGE_MODE"
      :scrollToBottom="true"
      dimmed
    >
      <div class="message-center">
        <div class="message-center-header">
          <span v-translate>Messages</span> <font-awesome-icon class="menu-close" @click="closeMessageCenter" :icon="['fas', 'times']" />
        </div>
        <div class="message-center-thread">
          <MessagesList
            :messages="messages"
            @next-page-loaded="markAllMessagesRead"
          />
        </div>
        <div class="message-center-form">
          <MessageForm :user="currentRequest.employeeRef" :request="currentRequest" @created="onMessageCreated" />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import LoadSpinner from '@/components/LoadSpinner'
import MessageForm from '@/views/common/MessageForm'
import MessagesList from '@/views/common/MessagesList'
import CollectionManager from '@/services/collectionManager'
import Message from '@/services/messages'
import Request from '@/services/requests'
import Event from '@/services/events'
import { analyticsMixin } from '@/services/googleAnalytics'
import SurveyResponse from '@/services/surveyresponses'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import Modal from '@/components/Modal'
import UserEvent from '@/services/userEvents'

const EVENT_DETAIL_MODE = 'EVENT_DETAIL'
const MESSAGE_MODE = 'MESSAGE'
const COVID_19 = 'COVID-19'

export default {
  name: 'RequestSummary',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    MessageForm,
    MessagesList,
    RequestStepProgressBar,
    Modal,
  },
  data() {
    return {
      EVENT_DETAIL_MODE,
      MESSAGE_MODE,
      mode: EVENT_DETAIL_MODE,

      globalLoading: false,
      loading: false,
      amount: null,
      requestSummary: null,
      events: [],
      messages: this.getMessages(),
    }
  },
  async created() {
    this.globalLoading = true
    await this.refresh()
    this.globalLoading = false
    this.pageview()
  },
  mounted() {
    // Add the request submit reminder event, if this request isn't submitted in 24 hours
    // this event will trigger a email reminder on the backend.
    const userEvent = UserEvent.create({
      eventType: UserEvent.options.REQUEST_SUBMIT_REMINDER,
      request: this.currentRequest.id,
      verificationAttempts: null,
    })
    UserEvent.api.create(userEvent)

    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_SUMMARY,
    )
  },
  methods: {
    getMessages() {
      const rawMessages = CollectionManager.create({
        ModelClass: Message,
        filters: {
          ordering: 'datetime_created',
          message_type: `${Message.options.DIGITAL_MESSAGE_ENGLISH},${Message.options.DIGITAL_MESSAGE_SPANISH},${Message.options.DIGITAL_MESSAGE}`,
        },
      })
      return rawMessages
    },
    messageUnreadCount() {
        return this.messages.list.filter((message) => message.isUnread).length
    },
    orderRequestQuestions() {
      var last_item = this.currentRequest.requestQuestions.splice(
        this.currentRequest.requestQuestions.findIndex((question) => question.order == -1),
        1,
      )
      this.currentRequest.requestQuestions.sort((first, second) => first.order - second.order)
      this.currentRequest.requestQuestions = this.currentRequest.requestQuestions.concat(last_item)
    },
    getSelectedOption(answer) {
      var choices = {
        YES: this.$gettext('Yes'),
        NO: this.$gettext('No'),
        "I DON'T KNOW": this.$gettext("I don't know"),
      }

      return (
        (Object.keys(choices) && Object.keys(choices).includes(answer) && choices[answer]) || answer
      )
    },
    pageview() {
      // trigger custom-pageview based on current mode
      let pageTitle = 'RequestSummary'
      let pagePath =
        this.mode === EVENT_DETAIL_MODE ? '/request-summary/details' : '/request-summary/messages'
      this.trackCustomPageview(pageTitle, pagePath)
    },
    async refresh() {
      //Fetch the most recent data from the API, because why not
      const request = await Request.api.retrieve(this.currentRequest.id)
      this.$store.dispatch('updateCurrentRequest', request)
      this.orderRequestQuestions()
      await this.refreshMessages()

      // Cast events as Event() to get access to model methods
      this.events = this.currentRequest.eventsRef.map((e) => Event.create(e))
    },
    async refreshMessages() {
      if (this.currentRequest) {
        this.messages.filters.request = this.currentRequest.id
        return this.messages.refresh()
      }
    },
    filterMessages(messages) {
      return rawMessages.filter((message) => {
        [
          Message.options.DIGITAL_MESSAGE,
          Message.options.DIGITAL_MESSAGE_ENGLISH,
          Message.options.DIGITAL_MESSAGE_SPANISH,
        ].includes(message.messageType)
      })
    },
    onMessageCreated(message) {
      this.messages.list.push(message)

      // Scroll down
      setTimeout(() => {
        let modalContainer = document.getElementById("modal_container")
        modalContainer.scrollTop = modalContainer.scrollHeight
      }, 250)
    },
    async onSubmitRequest() {
      this.loading = true
      this.trackClickedCTA('submit')

      const request = Request.create({
        ...this.currentRequest,
        status: Request.options.SUBMITTED,
        expenseDocumentationEnabled: this.organizationDetails.expenseDocumentationEnabled,
      })
      await Request.api.updateStatus(request)

      this.$router.push({ name: 'RequestSubmitted' })
      this.loading = false
    },
    /* Take user to the first legit request screen to start editing */
    async onEdit() {
      this.trackClickedCTA('edit')
      this.$router.push({ name: await this.nextRoute('PayPalVerify') })
    },
    async nextRoute(noSurveysRoute) {
      const householdDetailsSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Household Details')
      const finHealthSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(this.user, 'Financial Health')

      // if both surveys are complete
      if (householdDetailsSurveyComplete && finHealthSurveyComplete) {
        return noSurveysRoute
      }

      // if both surveys are disabled
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        !this.organizationDetails.isFinancialHealthSurveyEnabled
      ) {
        return noSurveysRoute
      }

      // if house hold details survey is enabled and complete
      if (
        !this.organizationDetails.isFinancialHealthSurveyEnabled &&
        this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        householdDetailsSurveyComplete
      ) {
        return noSurveysRoute
      }

      // if finhealth survey is enabled and complete
      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        finHealthSurveyComplete
      ) {
        return noSurveysRoute
      }

      if (this.organizationDetails.isHouseholdDetailsSurveyEnabled && !householdDetailsSurveyComplete) {
        return 'HouseholdDetailsRequest'
      }

      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled &&
        !finHealthSurveyComplete
      ) {
        return 'FinancialHealthRequest'
      }

      return 'HouseholdDetailsRequest'
    },
    markAllMessagesRead() {
      this.messages.list
        .filter((m) => m.isUnread)
        .forEach((m) => {
          m.isUnread = false
          Message.api.markRead(m).catch(() => {
            m.isUnread = true
          })
        })
      this.refresh()
    },
    openMessageCenter() {
      this.mode = MESSAGE_MODE
    },
    closeMessageCenter() {
      this.mode = EVENT_DETAIL_MODE
    },
  },
  computed: {
    ...mapState(['user', 'currentRequest', 'organizationDetails', 'requestHasUncommonEventCategories']),
    ...mapGetters(['organizationCssVars']),
    expenseDocRequired() {
      const empty = (doc) => doc.documentsRef.length === 0
      return (
        this.organizationDetails.expenseDocumentationEnabled &&
        this.currentRequest.expensesRef.every(empty) &&
        this.currentRequest.amount > 200
      )
    },
    eventDocRequired() {
      const empty = (doc) => doc.documentsRef.length === 0
      return this.currentRequest.eventsRef.every(empty)
    },
    disabled() {
      return (
        this.loading ||
        this.expenseDocRequired ||
        (this.organizationDetails.eventDocumentationEnabled && this.eventDocRequired)
      )
    },
    eventCategories() {
      return this.currentRequest.eventsRef.map((event) => event.categoryRef.name)
    },
    requestAlreadySubmitted() {
      return this.currentRequest.status != Request.options.STARTED
    },
    requestStartedOrSubmitted() {
      return (
        this.currentRequest.status === Request.options.STARTED ||
        this.currentRequest.status === Request.options.SUBMITTED
      )
    },
    eventsExceptCOVID() {
      return this.events.filter((e) => e.categoryRef.name !== COVID_19)
    },
    noAnswer() {
      return this.$gettext('- No Answer Provided -')
    },
    legacyRequestQuestionsCheck() {
      return (
        (this.currentRequest &&
          this.currentRequest.eventsFinancialHardshipAnswer &&
          this.currentRequest.eventsFinancialHardshipAnswer != '') ||
        (this.currentRequest.eventsMisc && this.currentRequest.eventsMisc != '') ||
        (this.currentRequest.eventsUnexpectedOrUncontrollableMisc &&
          this.currentRequest.eventsUnexpectedOrUncontrollableMisc != '') ||
        (this.currentRequest.eventsBasicLivingDifficultiesMisc &&
          this.currentRequest.eventsBasicLivingDifficultiesMisc != '')
      )
    },
    canEditRequest() {
      return (this.currentRequest.status !== Request.options.DECLINED &&
        this.currentRequest.status !== Request.options.APPROVED &&
        this.currentRequest.status !== Request.options.PARTIALLY_APPROVED)
    }
  },
  watch: {
    mode(newVal) {
      this.pageview()
      if (newVal === MESSAGE_MODE) {
        // Mark all unread messages as "read" when the user
        // switches to the 'Messages' view.
        this.markAllMessagesRead()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.request-summary {
  display: flex;
  flex-direction: column;

  .request-summary-form {
    @extend .constrain-tablet-and-up;

    @media (min-width: 1140px) {
      max-width: 1076px !important;
    }
  }

  .form-field-header {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .document-requirements {
    margin-top: 1.5rem;

    ul {
      padding-left: 0.5rem;
    }

    ul > li {
      font-size: 1.125rem;
      font-weight: bold;
      line-height: 1.5rem;
      margin-bottom: 1.5rem;

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  .request-details {
    margin-bottom: 2.5rem;

    .event-details, .expense-details {
      margin-bottom: 1.5rem;
    }

    .form-field-header {
      display: inline-block;
      margin-bottom: 1.5rem;
    }

    .edit-link {
      display: inline-block;
      float: right;
      font-size: 0.875rem;
      padding-top: 0.5rem;

      a {
        color: black;
      }
    }

    .form-field-container {
      margin-top: 0;
      margin-bottom: 1.5rem;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    .event-category-name {
      font-size: 1.125rem;
      line-height: 1.5rem;
      font-weight: bold;
    }

    .upload-link {
      color: #0067B9 !important;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .total-grant-amount {
      background-color: var(--dark-brand-color);
      color: white;
      padding: 1.5rem;

      .label {
        font-size: 1.125rem;
      }

      .amount {
        font-size: 2.25rem;
        font-weight: bold;
        line-height: 2.8rem;
      }
    }
  }

  .view-messages {
    text-align: center;
    font-size: 1.125rem;
    font-weight: bold;
    line-height: 1.5rem;
    vertical-align: center;

    a {
      font-weight: bold !important;
    }

    .unread-messages {
      background-color: #D90F10;
      border-radius: 0.75rem;
      color: white;
      cursor: pointer;
      display: inline-block;
      font-size: 1rem;
      font-weight: normal;
      margin-left: 0.5rem;
      padding: 0 0.4rem;
      position: relative;
      top: -2px;
    }
  }

  .message-center {
    background-color: white;
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 100vh;

    .message-center-header {
      background: white;
      box-shadow: 0 2px 4px 0 rgba(0,0,0,0.1);
      font-size: 1.5rem;
      font-weight: bold;
      line-height: 2rem;
      padding: 1rem 1.5rem;
      position: sticky;
      top: 0px;

      .menu-close {
        cursor: pointer;
        float: right;
      }
    }

    .message-center-thread {
      background-color: white;
      padding: 0 1rem;
    }

    .message-center-form {
      background: white;
      border-top: 1px solid #DFDFE0;
      position: sticky;
      bottom: 0px;
      width: 100%;
      padding: 1rem;
    }
  }
}

/*
 *
 *
 *
.request-summary {
  @include screen-container(0);
}
.info-box {
  margin: 2rem;
}
.buttons-container {
  margin-top: 1rem;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.button {
  @include primary-button(var(--dark-brand-color));
  min-width: 30%;
}
.disabled-button {
  @include primary-button-disabled();
  min-width: 30%;
}
.selected-button {
  @include primary-button-selected(var(--dark-brand-color));
  // NIP 3/14/20 I have no idea why, but without this left pixel offset,
  // this selected button leans slightly to the right
  left: -4px;
  min-width: 30%;
  margin-right: 1rem;
}
.summary {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.loading-screen {
  margin: 3rem;
  height: 100vh;
}
.section {
  width: 100%;
  margin: 1.5rem 0;
}
.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: var(--dark-brand-color);
  padding: 1rem 1.5rem;
  color: $white;
  margin-bottom: 1rem;

  h3 {
    color: $white;
    margin-bottom: 0px;
  }

  a {
    color: $white;
    text-decoration: underline;
  }
}
.sub-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: var(--light-brand-color);
  padding: 1rem 1.5rem;
  color: $black;

  h4 {
    color: $black;
    margin-bottom: 0px;
  }

  a {
    color: $black;
    text-decoration: underline;
  }
}
.title {
  color: $base-gray;
  margin-bottom: 0.8rem;
  margin-left: 1.5rem;
}
.entry {
  font-weight: 600;
  margin: 0.25rem 1.5rem 1.5rem 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid $soft-gray;
}
.question {
  color: $base-gray;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.answer {
  font-weight: 200;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid $soft-gray;
}
.q-and-a {
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding-right: 1rem;
}
.indent {
  margin-left: 2rem;
}
.text-error-msg {
  margin-left: 2rem;
  margin-right: 2rem;
}
.documentation-title {
  color: $black;
  font-weight: 600;
}
.documentation-entry {
  color: $base-gray;
  font-weight: 300;
}
.documentation-indent {
  margin-left: 1.5rem;
}
.unread-messages-icon {
  margin-left: -1.5rem;
  margin-top: -0.3rem;
  z-index: 99;
}
.instructions {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  padding: 2rem 1rem 3rem 1rem;
  background-color: var(--light-brand-color);

  ul {
    margin-top: 1.5rem;
  }
  li {
    font-size: 0.9rem;
  }
  span {
    font-size: inherit;
    font-weight: 600;
  }
}
.text-one-rem {
  font-size: 1rem;
}
.expense {
  font-weight: 600;
  margin: 0.25rem 1.5rem 1.5rem 1.5rem;
  padding-bottom: 0.5rem;
  display: flex;
  flex-flow: row;
  align-items: center;

  i.md-icon {
    color: var(--dark-brand-color);
    height: 1rem;
    width: 1rem;
    margin-left: 1rem;
  }
}
.emergency-card {
  display: flex;
  flex-flow: row;
  align-items: center;
  width: 80%;
  box-shadow: 0 4px 8px 0 $very-light-gray;
  border-radius: 0.5rem;
  height: 5rem;
  padding: 0 1rem;
  margin-top: 1rem;

  .icon {
    height: 2rem;
    width: 3rem;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  p {
    font-weight: 600;
    margin-left: 1rem;
  }
}
*/
</style>
